import { useState, useEffect, useCallback, useRef } from 'react';
import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/constants';
import { fetchAllStatistics } from '@/features/statistics/fetchAllStatistics';
import type { SystemInfo, ServiceHealth, GatewayHealthStatus } from '../types';

/** Refresh period while the tab is visible. Nothing is polled while it is hidden. */
export const POLL_INTERVAL = 120_000;

/** Coming back to the tab refreshes at once, unless the data is younger than this. */
export const MIN_REFRESH_AGE = 30_000;

interface UseSystemInfoReturn {
  systemInfo: SystemInfo | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

async function pingEndpoint(url: string): Promise<{ reachable: boolean; latencyMs: number; error?: string }> {
  const start = performance.now();
  try {
    await fetch(url, { method: 'GET', signal: AbortSignal.timeout(5000) });
    return { reachable: true, latencyMs: Math.round(performance.now() - start) };
  } catch (e: any) {
    return { reachable: false, latencyMs: Math.round(performance.now() - start), error: e.message };
  }
}

/** Requests and error rate of every account over the last 7 days; `null` when the statistics
 *  could not be read. The rate is `null` when there were no requests. */
async function fetchWeeklyActivity(): Promise<{ requests: number; errorRate: number | null } | null> {
  try {
    const stats = await fetchAllStatistics();
    const weekOk = stats.reduce((s, e) => s + (e.lastWeekSuccessfulAccess || 0), 0);
    const weekErr = stats.reduce((s, e) => s + (e.lastWeekWrongAccess || 0), 0);
    const weekTotal = weekOk + weekErr;
    return { requests: weekTotal, errorRate: weekTotal > 0 ? (weekErr / weekTotal) * 100 : null };
  } catch {
    return null;
  }
}

/** Maps both old format (primaryServer/secondaryServer) and new format (gateway/ocr/localCapabilities) */
function normalizeHealthResponse(raw: any): GatewayHealthStatus {
  // New format from /health/status — already has the right shape
  if (raw.ocr !== undefined) return raw as GatewayHealthStatus;

  // Old format from /admin/test/health: { primaryServer, secondaryServer }
  const primary = raw.primaryServer
    ? { name: raw.primaryServer.name, healthy: raw.primaryServer.healthy ?? raw.primaryServer.status === 'Online' }
    : null;
  const isSameServer = raw.secondaryServer && raw.primaryServer
    && raw.secondaryServer.name === raw.primaryServer.name;
  const secondary = raw.secondaryServer && !isSameServer
    ? { name: raw.secondaryServer.name, healthy: raw.secondaryServer.healthy ?? raw.secondaryServer.status === 'Online' }
    : null;
  const available = (primary?.healthy || secondary?.healthy) ?? false;

  return {
    gateway: 'healthy',
    ocr: { available, primary, secondary },
    // The old endpoint does not report capabilities: unknown, not assumed available.
    localCapabilities: null,
  };
}

async function fetchGatewayHealthStatus(accountsRes: PromiseSettledResult<any>): Promise<GatewayHealthStatus | null> {
  try {
    if (accountsRes.status !== 'fulfilled') return null;
    const firstAccount = accountsRes.value.data?.data?.[0];
    const firstAccountName = firstAccount?.accountResponse?.accountName ?? firstAccount?.name;
    if (!firstAccountName) return null;
    const healthRes = await httpClient.get<any>(
      `/admin/test/health?accountName=${encodeURIComponent(firstAccountName)}`
    );
    return normalizeHealthResponse(healthRes.data);
  } catch {
    return null;
  }
}

function ocrServerToCheck(kind: 'ocr-primary' | 'ocr-secondary', server: { name: string; healthy: boolean }, now: string): ServiceHealth {
  return { kind, name: server.name, status: server.healthy ? 'healthy' : 'down', latencyMs: null, lastChecked: now };
}

function buildHealthChecks(
  gwPing: { reachable: boolean; latencyMs: number; error?: string } | null,
  gatewayHealth: GatewayHealthStatus | null,
  servers: any[],
): ServiceHealth[] {
  const now = new Date().toISOString();
  const checks: ServiceHealth[] = [{
    kind: 'gateway',
    name: '',
    status: gwPing?.reachable ? 'healthy' : 'down',
    latencyMs: gwPing?.latencyMs ?? null,
    lastChecked: now,
    error: gwPing?.error,
  }];

  if (gatewayHealth?.ocr) {
    if (gatewayHealth.ocr.primary) checks.push(ocrServerToCheck('ocr-primary', gatewayHealth.ocr.primary, now));
    if (gatewayHealth.ocr.secondary) checks.push(ocrServerToCheck('ocr-secondary', gatewayHealth.ocr.secondary, now));
    return checks;
  }

  // Fallback: use admin server list when /health/status is unavailable
  for (const s of servers) {
    const online = s.status === 'Online' && !s.isForcedOffline;
    checks.push({ kind: 'server', name: s.name || String(s.id), status: online ? 'healthy' : 'down', latencyMs: null, lastChecked: now });
  }
  return checks;
}

const buildSystemInfo = async (): Promise<SystemInfo> => {
  const [accountsRes, serversRes, gatewayPing] = await Promise.allSettled([
    httpClient.get<any>('/admin/account/GetAccounts?draw=1&length=1&start=0&search=&column=&orderBy=asc'),
    httpClient.get<any>('/admin/server/GetServers?draw=1&length=100&start=0&search=&column=&orderBy=asc'),
    pingEndpoint(`${API_CONFIG.GATEWAY_URL}/health`),
  ]);

  const totalAccounts = accountsRes.status === 'fulfilled' ? (accountsRes.value.data?.recordsTotal ?? null) : null;
  const serversData = serversRes.status === 'fulfilled' ? serversRes.value.data : null;
  const servers = serversData?.data || [];
  const totalServers = serversData ? (serversData.recordsTotal ?? servers.length) : null;
  const onlineServers = serversData ? servers.filter((s: any) => s.status === 'Online' && !s.isForcedOffline).length : null;

  const [weekly, gatewayHealth] = await Promise.all([
    fetchWeeklyActivity(),
    fetchGatewayHealthStatus(accountsRes),
  ]);

  const gwPing = gatewayPing.status === 'fulfilled' ? gatewayPing.value : null;

  return {
    application: {
      version: __APP_VERSION__,
      environment: import.meta.env.MODE || 'development',
      gatewayUrl: API_CONFIG.GATEWAY_URL,
    },
    healthChecks: buildHealthChecks(gwPing, gatewayHealth, servers),
    gatewayHealth,
    summary: {
      totalServers,
      onlineServers,
      totalAccounts,
      weeklyRequests: weekly?.requests ?? null,
      weeklyErrorRate: weekly?.errorRate ?? null,
    },
  };
};

export const useSystemInfo = (): UseSystemInfoReturn => {
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastRefreshRef = useRef(0);

  const refresh = useCallback(async () => {
    lastRefreshRef.current = Date.now();
    try {
      const info = await buildSystemInfo();
      setSystemInfo(info);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Error loading system info');
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll only while the tab is visible: a hidden tab stops asking, and coming back refreshes
  // at once (unless the data is still fresh) before polling resumes.
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    const start = () => {
      if (timer === undefined) timer = setInterval(refresh, POLL_INTERVAL);
    };
    const stop = () => {
      if (timer !== undefined) {
        clearInterval(timer);
        timer = undefined;
      }
    };
    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
        return;
      }
      if (Date.now() - lastRefreshRef.current >= MIN_REFRESH_AGE) refresh();
      start();
    };

    refresh();
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refresh]);

  return { systemInfo, loading, error, refresh };
};
